import { useQuery } from "@tanstack/react-query";
import { useEffect, useRef } from "react";
import type { Asset, Space, TimeRange } from "@scenix/core";
import { api } from "../lib/api";

interface MomentPlayerProps {
  spaceId: Space["id"];
  assetId: Asset["id"];
  range: TimeRange;
}

/**
 * One moment's range, played where it is.
 *
 * A shortlist is a pile of moments somebody is considering, and deciding
 * between them means watching them. Following a link to the asset page to do
 * that loses the pile, which is the thing being decided.
 *
 * Plays the range and stops at the end of it rather than running on into the
 * rest of the asset: the moment is what somebody chose, and a player that keeps
 * going is showing them something they did not ask for.
 */
export function MomentPlayer({ spaceId, assetId, range }: MomentPlayerProps) {
  const video = useRef<HTMLVideoElement>(null);

  // Shares a cache entry with the asset page, so opening one after the other
  // costs a single read.
  const detail = useQuery({
    queryKey: ["asset", spaceId, assetId],
    queryFn: () => api.getAsset(spaceId, assetId),
  });

  const mediaUrl = detail.data?.mediaUrl ?? "";

  useEffect(() => {
    const element = video.current;
    if (!element || !mediaUrl) return;

    // Seeking before the metadata has arrived is ignored, so this waits for it
    // rather than hoping the load was fast.
    const start = () => {
      element.currentTime = range.startSec;
      // play() resolves a promise in every current browser and returns nothing
      // in the shape the spec used to have, which is what jsdom implements. The
      // difference is only visible when the return value is used, and calling
      // .catch on undefined throws right here in the listener.
      const started = element.play() as Promise<void> | undefined;
      started?.catch(() => {
        // Autoplay refused, which is the browser's to decide. The controls are
        // there and the playhead is in the right place.
      });
    };
    const stopAtTheEnd = () => {
      if (element.currentTime >= range.endSec) element.pause();
    };

    if (element.readyState >= 1) start();
    else element.addEventListener("loadedmetadata", start, { once: true });
    element.addEventListener("timeupdate", stopAtTheEnd);

    return () => {
      element.removeEventListener("loadedmetadata", start);
      element.removeEventListener("timeupdate", stopAtTheEnd);
    };
  }, [mediaUrl, range.startSec, range.endSec]);

  if (detail.isPending) return <p className="muted">Loading…</p>;
  if (!mediaUrl) {
    return <p className="muted">This moment&rsquo;s content cannot be played just now.</p>;
  }

  return (
    <video
      ref={video}
      className="moment-player"
      src={mediaUrl}
      controls
      preload="metadata"
      aria-label="This moment"
    />
  );
}
